<template>
    <v-app id="app" :class="{ 'app-transparent': $route.name === 'LiveData' }">
        <div v-if="$route.name === 'LiveData'">
            <router-view />
        </div>
        <v-container v-else class="bg mx-0 px-0" :class="'my-0 py-0'" fluid>
            <nav-bar v-if="$route.name !== 'FacebookPostMatch'" />
            <!-- room for the fixed bar and its gradient line -->
            <div v-if="$route.name !== 'FacebookPostMatch'" class="cl-nav-spacer" :class="{ 'cl-nav-spacer--mobile': $vuetify.breakpoint.mobile }" />
            <results-carousel v-if="false" :matches="matches" class="my-0 py-0" />
            <v-row no-gutters class="py-0 my-0">
                <!-- <aegean-ball v-if="!($route.name === 'Team' || $route.name === 'Match')"/> -->
                <v-col cols="12" class="py-0 my-0 px-0">
                    <router-view />
                </v-col>
            </v-row>
        </v-container>
        <custom-footer v-if="$route.name !== 'LiveData' && $route.name !== 'FacebookPostMatch'" />
        <!-- back to the top: appears once the page has scrolled -->
        <transition name="cl-top">
            <button
                v-show="fab"
                type="button"
                class="cl-top"
                :class="{ 'cl-top--mobile': $vuetify.breakpoint.mobile }"
                aria-label="Στην αρχή της σελίδας"
                @click="toTop"
            >
                <v-icon color="white">mdi-arrow-up</v-icon>
            </button>
        </transition>
        <span v-scroll="onScroll" class="d-none" />
    </v-app>
</template>

<script>
    import NavBar from '@/components/NavBar.vue'
    import CustomFooter from '@/components/Footer.vue'
    import ResultsCarousel from '@/components/ResultsCarousel.vue'

    export default {
        name: 'App',
        components: {
            NavBar,
            CustomFooter,
            ResultsCarousel
        },
        data() {
            return {
                fab: false,
                matches: []
            }
        },
        watch: {
            $route() {
                document.title = `${this.$route.meta} | Commercial League`
                this.syncTransparentBody()
            }
        },
        mounted() {
            this.syncTransparentBody()
        },
        created() {
            this.ensureConfig()
        },
        methods: {
            ensureConfig() {
                return this.$store.dispatch('ensureConfig')
            },
            onScroll(e) {
                if (typeof window === 'undefined') return
                const top = window.pageYOffset || e.target.scrollTop || 0
                this.fab = top > 20
            },
            toTop() {
                window.scrollTo({ top: 0, behavior: 'smooth' })
            },
            syncTransparentBody() {
                const live = this.$route?.name === 'LiveData'
                document.body.classList.toggle('body-transparent', !!live)
            }
        }
    }
</script>

<style>
@import url('https://fonts.googleapis.com/css?family=Squada+One&display=swap');
@import url('https://fonts.googleapis.com/css?family=Roboto:400,500,700,900&display=swap');
@import url('https://fonts.googleapis.com/css?family=Roboto+Condensed:700&subset=greek&display=swap');

/* ------------------------------------------------------------------
   Commercial League design system
   charcoal (#232323) and the teal-to-blue gradient of the league on a
   white canvas; Squada One for titles, Roboto for data
   ------------------------------------------------------------------ */
:root {
    --cl-ink: #232323;
    --cl-ink-2: #2e2e2e;
    --cl-teal: #009688;
    --cl-mint: #03e5b7;
    --cl-blue: #037ade;
    --cl-gradient: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    --cl-canvas: #fff;
    --cl-win: #00b894;
    --cl-loss: #f44336;
}

.left-rounded {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
}

.right-rounded {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
}

.cl-nav-spacer {
    height: 67px;
}

.cl-nav-spacer--mobile {
    height: 59px;
}

/* page content: centred, like the rest of the site */
.cl-page {
    max-width: 1240px;
    margin: 0 auto;
    padding: 28px 24px 0;
    text-align: left;
}

@media (max-width: 700px) {
    .cl-page {
        padding: 18px 10px 0;
    }
}

.cl-section {
    margin-bottom: 36px;
}

/* the dark cards of the league, a little softer */
.cl-card {
    border-radius: 10px !important;
    background: var(--cl-ink) !important;
    color: #fff !important;
    box-shadow: 0 8px 24px rgba(35, 35, 35, 0.12) !important;
    overflow: hidden;
}

.cl-card--light {
    background: #fff !important;
    color: var(--cl-ink) !important;
    border: 1px solid rgba(35, 35, 35, 0.08);
    box-shadow: 0 4px 16px rgba(35, 35, 35, 0.06) !important;
}

.cl-card--accent {
    border-top: 3px solid var(--cl-mint) !important;
}

.cl-display {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif !important;
    letter-spacing: 0.03em;
}

.cl-data {
    font-family: Roboto, Arial, sans-serif !important;
    font-variant-numeric: tabular-nums;
}

.cl-muted {
    color: rgba(255, 255, 255, 0.6) !important;
}

/* tables of the league: charcoal, readable figures */
.cl-table.v-data-table {
    border-radius: 10px !important;
    background: var(--cl-ink) !important;
    box-shadow: 0 8px 24px rgba(35, 35, 35, 0.12) !important;
    font-family: Roboto, Arial, sans-serif !important;
    font-variant-numeric: tabular-nums;
    overflow: hidden;
}

.cl-table.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    height: 42px;
    background: #1b1b1b !important;
    color: var(--cl-mint) !important;
    font-size: 0.7rem !important;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-bottom: 2px solid rgba(3, 229, 183, 0.35) !important;
}

.cl-table.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    height: 54px;
    font-size: 0.9rem !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
}

.cl-table.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
    background: rgba(3, 229, 183, 0.07) !important;
}

.cl-pos {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 6px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
}

.cl-pos--top {
    background: var(--cl-gradient);
}

.cl-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
}

.cl-logo img {
    max-width: 88%;
    max-height: 88%;
    object-fit: contain;
}

.cl-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
}

.cl-chip--gradient {
    background: var(--cl-gradient);
}

/* key figures (wins, losses...) */
.cl-kpis {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.cl-kpi {
    min-width: 76px;
    padding: 8px 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.08);
    text-align: center;
}

.cl-kpi__value {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.9rem;
    line-height: 1;
    color: #fff;
}

.cl-kpi__label {
    margin-top: 4px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.6);
}

.cl-kpi--accent {
    background: var(--cl-gradient);
    border-color: transparent;
}

.cl-kpi--accent .cl-kpi__label {
    color: rgba(255, 255, 255, 0.85);
}

/* page tabs (Standings / Match Center / ...) on the light canvas */
.cl-tabs.v-tabs > .v-tabs-bar {
    background: transparent !important;
    border-bottom: 1px solid rgba(35, 35, 35, 0.12);
}

.cl-tabs .v-tab {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif !important;
    font-size: 1.35rem !important;
    letter-spacing: 0.03em !important;
    text-transform: uppercase !important;
    color: rgba(35, 35, 35, 0.55) !important;
}

.cl-tabs .v-tab--active {
    color: var(--cl-ink) !important;
    -webkit-text-fill-color: currentColor;
    background: none;
}

.cl-tabs .v-tab::before {
    opacity: 0 !important;
}

.cl-tabs .v-tabs-slider-wrapper {
    height: 4px !important;
}

/* phones and tablets: tighter tabs that scroll with a swipe, no arrows, a fade where more tabs wait */
@media (max-width: 960px) {
    .cl-tabs.v-tabs > .v-tabs-bar {
        height: 44px !important;
    }

    .cl-tabs .v-slide-group__prev,
    .cl-tabs .v-slide-group__next {
        display: none !important;
    }

    .cl-tabs .v-slide-group__wrapper {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -webkit-mask-image: linear-gradient(90deg, #000 calc(100% - 18px), transparent);
        mask-image: linear-gradient(90deg, #000 calc(100% - 18px), transparent);
    }

    .cl-tabs .v-slide-group__content {
        padding-right: 18px;
    }

    .cl-tabs .v-tab {
        min-width: 0 !important;
        padding: 0 10px !important;
        font-size: 1rem !important;
        letter-spacing: 0.02em !important;
    }

    .cl-tabs .v-tab:first-child {
        padding-left: 4px !important;
    }

    .cl-tabs .v-tabs-slider-wrapper {
        height: 3px !important;
    }

    .cl-tabs .v-tabs-slider {
        border-radius: 3px 3px 0 0;
    }
}

/* the option chips used across the pages (averages / totals, players / teams...) */
.v-chip-group .v-chip.v-chip--clickable {
    height: 34px !important;
    padding: 0 16px !important;
    border-radius: 999px !important;
    font-family: Roboto, Arial, sans-serif !important;
    font-size: 0.85rem !important;
    font-weight: 700 !important;
    letter-spacing: 0.02em !important;
    box-shadow: 0 1px 4px rgba(35, 35, 35, 0.12);
}

.v-chip-group .v-chip.selected-chip {
    color: #fff !important;
    box-shadow: 0 2px 10px rgba(3, 122, 222, 0.35);
}

/* every charcoal table reads like the league tables */
.v-data-table.theme--dark {
    font-family: Roboto, Arial, sans-serif !important;
    font-variant-numeric: tabular-nums;
}

.v-data-table.theme--dark > .v-data-table__wrapper > table > thead > tr > th {
    color: var(--cl-mint) !important;
    font-size: 0.7rem !important;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
}

.v-data-table.theme--dark > .v-data-table__wrapper > table > tbody > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    background: rgba(3, 229, 183, 0.07) !important;
}

/* long texts (philosophy, regulations, policies) on a light page to read */
.cl-reading {
    padding: 32px 40px !important;
    font-family: Roboto, Arial, sans-serif;
    font-size: 1rem;
    line-height: 1.7;
    text-align: left;
}

.cl-reading h1,
.cl-reading h2,
.cl-reading h3 {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-weight: 400;
    letter-spacing: 0.02em;
    color: var(--cl-ink);
}

.cl-reading a {
    color: var(--cl-teal);
}

@media (max-width: 700px) {
    .cl-reading {
        padding: 20px 16px !important;
    }
}

.cl-empty {
    padding: 36px 16px;
    border-radius: 10px;
    border: 2px dashed rgba(35, 35, 35, 0.15);
    color: rgba(35, 35, 35, 0.55);
    font-family: Roboto, Arial, sans-serif;
    text-align: center;
}

.v-card {
    color: white;
    background: #232323;
}

.v-slide-group__prev {
    display: none !important;
}

.v-tab {
    color: white !important;
}

.v-tab--active {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.custom-teal {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}

.custom-teal-text {
    background-image: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

#app {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif !important;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
}

/* the page grows to push a short page's footer down; no fixed height (it counted the footer twice) */
.bg {
    flex: 1 0 auto;
    width: 100%;
    background: var(--cl-canvas);
}

.v-application {
    background: var(--cl-canvas) !important;
}

.v-data-table {
    font-family: Arial, sans-serif !important;
    font-weight: 400;
}

thead {
    white-space: nowrap;
}

.secondary-font {
    font-family: Arial, sans-serif !important;
    color: white;
    font-weight: 700;
}

/* width */
::-webkit-scrollbar {
    width: 5px;
    height: 5px;
}

/* Track */
::-webkit-scrollbar-track {
    background: rgba(35, 35, 35, 0.06);
    border-radius: 10px;
}

/* Handle */
::-webkit-scrollbar-thumb {
    background: #9aa5a4;
    border-radius: 10px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
    background: var(--cl-teal);
    border-radius: 10px;
}

::-webkit-scrollbar-thumb:horizontal {
    border-radius: 10px;
}

/* the back-to-top button */
.cl-top {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 14;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #232323;
    box-shadow: 0 10px 24px rgba(35, 35, 35, 0.3), inset 0 -3px 0 #03e5b7;
    transition: transform 0.15s, background 0.15s;
}

.cl-top:hover {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    box-shadow: 0 10px 24px rgba(3, 122, 222, 0.35);
    transform: translateY(-2px);
}

.cl-top--mobile {
    right: 14px;
    bottom: 14px;
    width: 42px;
    height: 42px;
}

.cl-top-enter-active,
.cl-top-leave-active {
    transition: opacity 0.2s, transform 0.2s;
}

.cl-top-enter,
.cl-top-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

.clickable {
    cursor: pointer !important;
}

table > tbody > tr > td:nth-child(1),
table > thead > tr > th:nth-child(1) {
    position: sticky !important;
    left: 0;
    background: #232323;
}

.app-transparent,
.app-transparent .v-application,
.app-transparent .v-application--wrap,
.app-transparent #app {
    background: transparent !important;
    background-color: transparent !important;
}

/* Also clear page background when LiveData */
.body-transparent {
    background: transparent !important;
    background-color: transparent !important;
}
</style>
